import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../auth';
import { useClientSelection } from '../client-context';
import { clientOptions, type PickerOption } from '../client-options';
import Dialog from '../components/Dialog';
import { useApi } from '../hooks';
import { EmptyState, ErrorState, monogram, PageHeader, PageSkeleton, StatusBadge } from '../components/Ui';
import QuickMenu, { QuickMenuItem } from '../components/QuickMenu';
import { ROLE_LABEL } from '../roles';

type Member = { id: number; full_name: string; role: string; open_tasks: number; due_today: number; overdue: number; estimated_hours_open: number; capacity_pct: number; status: string };
type Competency = { user_id: number; competency_name: string; level: string; certified_at: string | null; notes: string | null };
type Coverage = { user_id: number; client_ids: number[] };

const TABS = [['workload', 'Workload'], ['competencies', 'Competencies'], ['coverage', 'Client coverage']] as const;
type Tab = (typeof TABS)[number][0];
// Column names a reader would say out loud, not key_names with capitals.
const COMPETENCIES: [string, string][] = [
  ['ppc_paid_advertising', 'PPC / paid'], ['seo_aeo', 'SEO / AEO'], ['social_media', 'Social'], ['content_creative', 'Content & creative'],
  ['website_technical', 'Website & technical'], ['reporting_analytics', 'Reporting'], ['client_communication', 'Client comms'], ['nl_aos_system', 'NOLA'],
];
const LEVELS: { value: string; label: string; tone: string }[] = [
  { value: 'none', label: 'None', tone: 'ghost' },
  { value: 'learning', label: 'Learning', tone: 'warning' },
  { value: 'secondary', label: 'Secondary', tone: 'info' },
  { value: 'primary', label: 'Primary', tone: 'success' },
];
const label = (value: string) => value.replaceAll('_', ' ').replace(/^\w/, letter => letter.toUpperCase());
// The role names Settings > Members uses, so a person reads the same on both pages.
const ROLES: Record<string, string> = { ...ROLE_LABEL, queue: '' };
const roleLabel = (role: string) => ROLES[role] ?? label(role);
/** "A, B, C and 4 more": a long list stays one line; the full list is the cell's tooltip. */
const someNames = (names: string[], shown = 3) => names.length <= shown + 1 ? names.join(', ') : `${names.slice(0, shown).join(', ')} and ${names.length - shown} more`;

/**
 * One cell of a matrix: a coloured chip that opens a menu of the other values. The first
 * value (None) reads as an empty cell, so the levels people do have stand out; the menu
 * still names it, and the chevron shows on hover or focus.
 */
function MatrixChip({ value, options, onPick, canEdit, name }: {
  value: string; options: { value: string; label: string; tone: string }[]; onPick: (v: string) => void; canEdit: boolean; name: string;
}) {
  const current = options.find(o => o.value === value) || options[0];
  const chip = current === options[0]
    ? <span className="chip chip-ghost matrix-empty"><span className="sr-only">{current.label}</span>{canEdit && <ChevronDown aria-hidden="true" />}</span>
    : <span className={`chip chip-${current.tone}`}>{current.label}{canEdit && <ChevronDown aria-hidden="true" />}</span>;
  if (!canEdit) return <span className="qmenu-trigger is-static">{chip}</span>;
  return (
    <QuickMenu label={name} trigger={chip}>
      {(close) => options.map(o => (
        <QuickMenuItem key={o.value} active={o.value === current.value} onClick={() => { onPick(o.value); close(); }}>
          <span className={`chip chip-${o.tone} chip-sm`}>{o.label}</span>
        </QuickMenuItem>
      ))}
    </QuickMenu>
  );
}

/**
 * The clients one person covers, as a checklist: active clients first, then the
 * paused ones, marked. Saving replaces the whole list.
 */
function CoverageDialog({ person, covered, options, onClose, onSaved }: {
  person: Member; covered: number[]; options: PickerOption[]; onClose: () => void; onSaved: (message: string) => void;
}) {
  const [picked, setPicked] = useState(() => new Set(covered.map(String)));
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const firstName = person.full_name.split(/\s+/)[0] || person.full_name;
  const toggle = (value: string, on: boolean) => setPicked(current => { const next = new Set(current); if (on) next.add(value); else next.delete(value); return next; });
  async function save(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      await api(`/api/team/coverage/${person.id}`, { method: 'PUT', body: JSON.stringify({ client_ids: [...picked].map(Number) }) });
      onSaved(`${person.full_name} covers ${picked.size} ${picked.size === 1 ? 'client' : 'clients'}.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not save.'); }
    finally { setBusy(false); }
  }
  return (
    <Dialog title={`Clients ${firstName} covers`} description={`Ticked clients appear on ${firstName}’s morning standup.`} onClose={() => { if (!busy) onClose(); }}>
      <form className="operation-form" onSubmit={save}>
        <fieldset className="team-coverage-list">
          <legend className="sr-only">Clients</legend>
          {options.map(option => (
            <label className="checkbox-line" key={option.value}>
              <input type="checkbox" checked={picked.has(option.value)} disabled={busy} onChange={event => toggle(option.value, event.target.checked)} />
              <span className={option.active ? undefined : 'muted'}>{option.label}</span>
            </label>
          ))}
          {!options.length && <p className="workflow-note">No clients yet.</p>}
        </fieldset>
        {error && <p role="alert" className="inline-notice danger">{error}</p>}
        <div className="operation-actions">
          <span className="team-coverage-picked" aria-live="polite">{picked.size} selected</span>
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="button" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Dialog>
  );
}

export default function Team() {
  const { user } = useAuth();
  const { clients } = useClientSelection();
  const [tab, setTab] = useState<Tab>('workload');
  const [busy, setBusy] = useState(false), [feedback, setFeedback] = useState('');
  const [editing, setEditing] = useState<Member | null>(null);
  const isAdmin = user?.role === 'superadmin';
  const workload = useApi<Member[]>('/api/team/workload');
  const skills = useApi<Competency[]>(tab === 'competencies' ? '/api/team/competencies' : null);
  const coverage = useApi<Coverage[]>(tab === 'coverage' ? '/api/team/coverage' : null);
  async function update(endpoint: string, body: object, reload: () => void) {
    setBusy(true); setFeedback('');
    try { await api(endpoint, { method: 'PUT', body: JSON.stringify(body) }); reload(); setFeedback('Saved.'); }
    catch (error) { setFeedback(error instanceof Error ? error.message : 'Could not save.'); }
    finally { setBusy(false); }
  }
  const members = workload.data || [];
  const people = members.filter(person => person.id);
  // Capacity is estimated hours over a 40 hour week. With no estimates every bar
  // read "0% Available", which says nothing, so a person without any shows a quiet
  // "No estimates", and when nobody has any the capacity columns are left out.
  const hasEstimates = (person: Member) => Number(person.estimated_hours_open || 0) > 0;
  const showCapacity = members.some(hasEstimates);
  // A row opens that person's tasks in the Work queue. Staff other than a
  // superadmin see only their own tasks there, so only their own row opens for them.
  const navigate = useNavigate();
  const canOpen = (person: Member) => isAdmin || (person.id !== 0 && person.id === user?.id);
  const openQueue = (person: Member) => navigate(`/tasks?owner=${person.id ? person.id : 'none'}`);
  const capacityTone = (person: Member) => person.status === 'overloaded' || person.status === 'unassigned' ? 'danger' : person.status === 'near_capacity' ? 'warning' : 'neutral';
  const options = clientOptions(clients.data);
  const optionLabel = new Map(options.map(option => [option.value, option.label]));
  const coveredBy = (id: number) => coverage.data?.find(row => row.user_id === id)?.client_ids || [];
  const refreshing = workload.refreshing || skills.refreshing || coverage.refreshing;

  return <>
    <PageHeader eyebrow="Operations" title="Team" actions={<button className="button secondary" disabled={refreshing} onClick={() => { workload.reload(); skills.reload(); coverage.reload(); }}><RefreshCw className={refreshing ? 'spin' : ''} aria-hidden="true" />{refreshing ? 'Refreshing…' : 'Refresh'}</button>} />
    <div className="tabs" role="tablist" aria-label="Team views">{TABS.map(([value, name]) => <button key={value} role="tab" aria-selected={tab === value} className={tab === value ? 'active' : ''} onClick={() => { setTab(value); setFeedback(''); }}>{name}</button>)}</div>
    {feedback && <p role="status" className="inline-notice">{feedback}</p>}

    {tab === 'workload' && (workload.loading ? <PageSkeleton /> : workload.error ? <ErrorState message={workload.error} onRetry={workload.reload} /> : !members.length ? <EmptyState title="No active team members" description="Active staff will appear after their accounts are set up." /> : <>
      {members.some(person => person.status === 'overloaded') && <p className="panel-note">{members.filter(person => person.status === 'overloaded').map(person => person.full_name).join(', ')} exceed the 40 hour capacity baseline. Review assignments in the work queue.</p>}
      <div className="q-grid-wrap panel">
        <table className={showCapacity ? 'q-grid team-grid' : 'q-grid team-grid no-capacity'}>
          <thead><tr><th>Team member</th><th className="team-role">Role</th>{showCapacity && <th className="team-capacity-head">Capacity</th>}<th className="num">Open</th><th className="num">Due today</th><th className="num">Overdue</th>{showCapacity && <th className="num team-hours">Open hours</th>}<th className="q-grid-go"><span className="sr-only">Open tasks</span></th></tr></thead>
          <tbody>{members.map(person => {
            const opens = canOpen(person);
            const rowProps = opens
              ? { tabIndex: 0, onClick: () => openQueue(person), onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => { if (event.key === 'Enter' && event.target === event.currentTarget) openQueue(person); }, 'aria-label': person.id ? `Open ${person.full_name}’s tasks` : 'Open unassigned tasks' }
              : { className: 'is-static' };
            return <tr key={person.id || 'queue'} {...rowProps}>
              <td className="q-grid-title"><span className="team-member"><span className={`q-avatar${person.id ? '' : ' empty'}`} aria-hidden="true">{person.id ? monogram(person.full_name) : ''}</span>{person.full_name}{!showCapacity && person.status === 'unassigned' && <span className="team-unassigned-badge"><StatusBadge tone="danger">Needs owner</StatusBadge></span>}</span></td>
              <td className="q-grid-muted team-role">{roleLabel(person.role)}</td>
              {showCapacity && <td className="team-cap">
                {person.status === 'unassigned'
                  ? <StatusBadge tone="danger">Needs owner</StatusBadge>
                  : hasEstimates(person)
                    ? <span className="team-capacity"><span className="team-capacity-track" aria-hidden="true"><i className={capacityTone(person)} style={{ width: `${Math.min(100, Math.max(0, person.capacity_pct))}%` }} /></span><span className="team-capacity-text">{person.capacity_pct}%<small>{label(person.status)}</small></span></span>
                    : <span className="team-no-estimates">No estimates</span>}
              </td>}
              {/* data-label names each count where a phone shows them as one line: "6 open   1 due today". */}
              <td className="num team-count" data-label="open">{person.open_tasks}</td>
              <td className="num team-count" data-label="due today">{person.due_today}</td>
              <td className={`num team-count${Number(person.overdue) > 0 ? ' is-late' : ''}`} data-label="overdue">{person.overdue}</td>
              {showCapacity && <td className="num team-hours">{Number(person.estimated_hours_open || 0).toFixed(1)}</td>}
              <td className="q-grid-go">{opens && <ChevronRight aria-hidden="true" />}</td>
            </tr>;
          })}</tbody>
        </table>
        {showCapacity && <div className="q-grid-foot">Capacity is estimated hours on open tasks over a 40 hour week. Unestimated work counts in the task columns but not in the percentage.</div>}
      </div>
    </>)}

    {tab === 'competencies' && (workload.loading || skills.loading ? <PageSkeleton /> : workload.error || skills.error ? <ErrorState message={workload.error || skills.error || ''} onRetry={() => { workload.reload(); skills.reload(); }} /> : !people.length ? <EmptyState title="No team members" description="Add active staff before recording competencies." /> : <div className="q-grid-wrap panel">
      <table className="q-grid matrix">
        <thead><tr><th className="matrix-sticky">Team member</th>{COMPETENCIES.map(([key, name]) => <th key={key}>{name}</th>)}</tr></thead>
        <tbody>{people.map(person => <tr key={person.id} className="is-static">
          <td className="matrix-sticky q-grid-title">{person.full_name}</td>
          {COMPETENCIES.map(([key, name]) => {
            const existing = skills.data?.find(item => item.user_id === person.id && item.competency_name === key);
            return <td key={key}><MatrixChip name={`${person.full_name}: ${name}`} value={existing?.level || 'none'} options={LEVELS} canEdit={isAdmin && !busy} onPick={level => update('/api/team/competencies', { user_id: person.id, competency_name: key, level }, skills.reload)} /></td>;
          })}
        </tr>)}</tbody>
      </table>
      <div className="q-grid-foot">Primary is who takes the work first; Secondary covers; Learning is being trained.{!isAdmin && ' Only a superadmin can change these.'}</div>
    </div>)}

    {tab === 'coverage' && (workload.loading || coverage.loading || clients.loading ? <PageSkeleton /> : workload.error || coverage.error || clients.error ? <ErrorState message={workload.error || coverage.error || clients.error || ''} onRetry={() => { workload.reload(); coverage.reload(); clients.reload(); }} /> : !people.length ? <EmptyState title="No team members" description="Add active staff before choosing who covers which client." /> : <div className="q-grid-wrap panel">
      <table className="q-grid team-coverage">
        <thead><tr><th>Team member</th><th>Clients they cover</th>{isAdmin && <th className="q-grid-go"><span className="sr-only">Edit</span></th>}</tr></thead>
        <tbody>{people.map(person => {
          const names = coveredBy(person.id).map(id => optionLabel.get(String(id))).filter((name): name is string => Boolean(name));
          // An admin opens a person's row to tick their clients, like any other row in the app.
          const editable = isAdmin ? { tabIndex: 0, onClick: () => setEditing(person), onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setEditing(person); } }, 'aria-label': `Choose the clients ${person.full_name} covers` } : { className: 'is-static' };
          return <tr key={person.id} {...editable}>
            <td className="q-grid-title">{person.full_name}</td>
            <td className="team-coverage-clients" title={names.length > 4 ? names.join(', ') : undefined}>{names.length ? someNames(names) : isAdmin ? <span className="team-choose">Choose clients</span> : <span className="muted">No clients yet</span>}</td>
            {isAdmin && <td className="q-grid-go"><ChevronRight aria-hidden="true" /></td>}
          </tr>;
        })}</tbody>
      </table>
      <div className="q-grid-foot">Clients ticked here appear on that person’s morning standup. This does not limit what they can open.{!isAdmin && ' Only a superadmin can change these.'}</div>
    </div>)}
    {editing && <CoverageDialog person={editing} covered={coveredBy(editing.id)} options={options} onClose={() => setEditing(null)} onSaved={message => { setEditing(null); coverage.reload(); setFeedback(message); }} />}
  </>;
}
